import * as React from 'react';
import './participants.scss';
import { RouteComponentProps } from 'react-router';
import { Button, Loader } from '@teamsnap/teamsnap-ui';
import { TEAMSNAP_URL } from '../../core/constants';
import * as SeasonQuery from '../../core/gql/queries/show-season';
import * as SeasonCustomFieldsQuery from '../../core/gql/queries/season_custom_fields';
import * as ParticipantsQuery from '../../core/gql/queries/show-participants';
import * as MemberSeasonParticipantsQuery from '../../core/gql/queries/member_season_participants';
import * as MembersCustomDataQuery from '../../core/gql/queries/members_custom_data';
import { useQuery, useLazyQuery, NetworkStatus } from '@apollo/client';
import ParticipantsList from '../../components/participants/participants-list';
import { useReactiveVar } from '@apollo/client';
import {
  seasonMembersVar,
  seasonMemberContactsVar,
  seasonParicipationsVar,
  // seasonCustomDataVar,
  setLoaderWithRefresh,
  updateSeasonParticipantCache,
  stopRefreshers,
  exportParticipants,
} from '../../components/participants/season-participants-service';
import { generateCSVFileDownload, groupBy } from 'core/utils';
import { ActionMenu } from '@teamsnap/snap-ui';
import CSVLink from 'components/common/csv-link';
import { isEqual } from 'lodash';
import { reactiveFeedbackMessage } from 'core/apollo-cache';
import { AxiosResponse } from 'axios';

const getPageNumber = (customDataResponse) => customDataResponse?.membersCustomDataPaged?.metadata?.pageNumber;
const hasMoreCustomDataPages = (customDataResponse: any) =>
  getPageNumber(customDataResponse) < customDataResponse?.membersCustomDataPaged?.metadata?.totalPages;

export interface Props extends RouteComponentProps<any> {}

const getPhoneNumberByLabel = (phoneNumbers, label) => phoneNumbers.find((phone) => phone.label === label);

const validLlbSports = [5, 4, 61, 66]; // baseball, softball, slo-pitch and wheelchair softball

const ParticipantsPage: React.FC<Props> = ({
  match: {
    params: { orgId, seasonId, programId },
  },
  location,
}) => {
  React.useEffect(() => {
    setLoaderWithRefresh(
      'SeasonMembers',
      seasonMembersVar,
      `/organizations/${orgId}/seasons/${seasonId}/members`,
      'id'
    );
    setLoaderWithRefresh(
      'SeasonMemberContacts',
      seasonMemberContactsVar,
      `/organizations/${orgId}/seasons/${seasonId}/member_contacts`,
      'id'
    );
    setLoaderWithRefresh(
      'SeasonParticipations',
      seasonParicipationsVar,
      `/organizations/${orgId}/seasons/${seasonId}/participations`,
      'seasonParticipantId'
    );
    // Temporarily commented out since some season take so log to refresh
    // setLoaderWithRefresh(
    //   'SeasonCustomData',
    //   seasonCustomDataVar,
    //   `/organizations/${orgId}/seasons/${seasonId}/custom_data`,
    //   'id'
    // );
    return function cleanup() {
      stopRefreshers();
    };
  }, [orgId, seasonId]);

  const [completed, setCompleted] = React.useState(false);
  const [retries, setRetries] = React.useState(0);
  const [membersToExport, setMembersToExport] = React.useState([]);
  const [membersCustomData, setMembersCustomData] = React.useState([]);
  const [loadingMembersCustomData, setLoadingMembersCustomData] = React.useState(true);
  const [exportIsOpen, setExportIsOpen] = React.useState(false);
  const [isExporting, setIsExporting] = React.useState(false);
  const [filteredParticipantIds, setFilteredParticipantIds] = React.useState<number[]>([]);
  const exportButtonRef = React.useRef(null);

  const { data: seasonData } = useQuery(SeasonQuery.QUERY, {
    variables: { seasonId: +seasonId },
  });

  React.useEffect(() => {
    document.title = `TeamSnap :: ${seasonData?.showSeason?.name} :: Participants`;
  }, [seasonData]);

  let seasonParticipantId;
  if (location.search && location.search.length) {
    const queryParams = new URLSearchParams(location.search);
    seasonParticipantId = queryParams.get('season_participant_id');
  }

  const {
    data: memberSeasonParticipantData,
    startPolling,
    stopPolling,
    networkStatus,
  } = useQuery(MemberSeasonParticipantsQuery.QUERY, {
    variables: {
      organizationId: orgId,
      seasonParticipantId: +seasonParticipantId,
      seasonParticipantType: 'snapi_roster',
    },
    skip: !seasonParticipantId,
    notifyOnNetworkStatusChange: true,
  });

  const memberSeasonParticipant = memberSeasonParticipantData?.memberSeasonParticipants[0];
  const memberId = memberSeasonParticipant?.member.id;
  const { data: fetchedNewMember } = useQuery(ParticipantsQuery.SHOW_SEASON_PARTICIPANT, {
    variables: { seasonId: seasonId, memberId: +memberId, organizationId: +orgId },
    skip: !memberId,
  });

  React.useEffect(() => {
    if (memberSeasonParticipant) {
      setCompleted(true);

      if (fetchedNewMember) {
        // eventually may need to update contacts but can't update contact info on current create
        updateSeasonParticipantCache([fetchedNewMember.member]);
      }
    } else if (networkStatus === NetworkStatus.poll) {
      setRetries(retries + 1);
    }
  }, [memberSeasonParticipant, networkStatus, fetchedNewMember]);

  React.useEffect(() => {
    if (!completed && retries < 10) {
      startPolling(3000);
    } else {
      stopPolling();
    }
    return () => {
      stopPolling();
    };
  }, [stopPolling, startPolling, completed, retries]);

  const seasonMembers = useReactiveVar(seasonMembersVar);
  const seasonMembersData = seasonMembers.data;
  const seasonParicipations = useReactiveVar(seasonParicipationsVar);
  const seasonParicipationsData = seasonParicipations.data;
  const seasonMemberContacts = useReactiveVar(seasonMemberContactsVar);
  const memberContactsData = seasonMemberContacts.data;
  const showLlbExport = validLlbSports.includes(seasonData?.showSeason?.program?.sportId);

  const isLoading = seasonMembers.isLoading || seasonParicipations.isLoading;

  const divisionParam = seasonData?.showSeason?.tsDivisionId;

  const tsDivisionId = seasonData?.showSeason?.tsDivisionId;
  const { data: seasonCustomFieldsData } = useQuery(SeasonCustomFieldsQuery.QUERY, {
    variables: { tsDivisionId: +divisionParam },
    skip: !tsDivisionId,
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  });

  const customFields = React.useMemo(
    () =>
      seasonCustomFieldsData?.customFields.map((x) => ({
        id: x.id,
        name: `${x.id}|${x.name}`,
        label: x.name,
        isSortable: false,
        selected: false,
        style: { width: '15%' },
      })),
    [seasonCustomFieldsData]
  );

  //Get member custom data
  const [loadCustomData, { fetchMore: fetchMoreCustomData }] = useLazyQuery(MembersCustomDataQuery.QUERY, {
    variables: { tsDivisionId: +divisionParam, pageNumber: 0, pageSize: 5000 },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  });

  React.useEffect(() => {
    const loadMoreCustomData = (pageNumber, membersCustomData) => {
      fetchMoreCustomData({
        variables: {
          pageNumber: pageNumber,
        },
      }).then(({ data }: { data: any }) => {
        const updatedMembersCustomData = membersCustomData.concat(data.membersCustomDataPaged.data);

        if (hasMoreCustomDataPages(data)) {
          loadMoreCustomData(pageNumber + 1, updatedMembersCustomData);
        } else {
          setMembersCustomData(updatedMembersCustomData);
          setLoadingMembersCustomData(false);
        }
      });
    };

    if (membersCustomData.length === 0 && tsDivisionId) {
      loadCustomData().then(({ data }: { data: any }) => {
        if (hasMoreCustomDataPages(data)) {
          loadMoreCustomData(2, data?.membersCustomDataPaged?.data);
        } else if (data?.membersCustomDataPaged) {
          setMembersCustomData(data?.membersCustomDataPaged?.data);
          setLoadingMembersCustomData(false);
        }
      });
    }
  }, [tsDivisionId]);

  const allMembers = React.useMemo(() => {
    const seasonParticipationsMap = groupBy('memberId', seasonParicipationsData);
    const contacts = !memberContactsData
      ? {}
      : memberContactsData.reduce((prev, curr) => {
          const name = `${curr.firstName} ${curr.lastName}`;

          prev[curr.memberId] = {
            names: prev[curr.memberId]?.names ? [...prev[curr.memberId].names, name] : [name],
            emailAddresses: prev[curr.memberId]?.emailAddresses
              ? [...(curr.emailAddresses.map((x) => x.email) || []), ...prev[curr.memberId]?.emailAddresses]
              : curr.emailAddresses.map((x) => x.email),
          };

          return prev;
        }, {});

    const customDataByParticipant = groupBy('memberId', membersCustomData || []);

    const data = seasonMembersData
      .filter((x) => seasonParicipations.isLoading || seasonParticipationsMap[x.id]?.length)
      .map((member) => ({
        ...member,
        contacts: contacts[member.id] || { names: [], emailAddresses: [] },
        seasonParticipants: seasonParticipationsMap[member.id]?.map((participant) => ({
          ...participant,
          ...(customFields || []).reduce((prev, curr) => {
            prev[curr.name] = customDataByParticipant[participant.seasonParticipantId]?.find(
              (x) => (x.customFieldId || x.leagueCustomFieldId) == curr.id
            )?.value;

            return prev;
          }, {}),
        })),
      }));

    return data;
  }, [seasonMembersData, seasonParicipationsData, memberContactsData, customFields, membersCustomData]);

  const csvHeader = [
    { label: 'Org Member Id', key: 'orgMemberId' },
    { label: 'first', key: 'first' },
    { label: 'last', key: 'last' },
    { label: 'role', key: 'role' },
    { label: 'address', key: 'address' },
    { label: 'city', key: 'city' },
    { label: 'state', key: 'state' },
    { label: 'zip', key: 'zip' },
    { label: 'birthdate', key: 'birthdate' },
    { label: 'Jersey Number', key: 'jerseyNumber' },
    { label: 'Position', key: 'position' },
    { label: 'email', key: 'email' },
    { label: 'phone_number', key: 'phoneNumber' },
    { label: 'gender', key: 'gender' },
  ];

  [1, 2, 3, 4].forEach((contactNumber) => {
    csvHeader.push(
      { label: `p${contactNumber}fn`, key: `p${contactNumber}fn` },
      { label: `p${contactNumber}ln`, key: `p${contactNumber}ln` },
      { label: `p${contactNumber}relation`, key: `p${contactNumber}relation` },
      { label: `p${contactNumber}email`, key: `p${contactNumber}email` },
      { label: `p${contactNumber}home`, key: `p${contactNumber}home` },
      { label: `p${contactNumber}cell`, key: `p${contactNumber}cell` },
      { label: `p${contactNumber}work`, key: `p${contactNumber}work` }
    );
  });

  csvHeader.push(
    { label: 'Team', key: 'teamName' },
    { label: 'Team ID', key: 'teamId' },
    { label: 'Division', key: 'division' },
    { label: 'Pending Division', key: 'pendingDivisionName' },
    { label: 'Pending Team', key: 'pendingTeamName' },
    { label: 'Registration Form', key: 'registrationForm' },
    { label: 'Registration ID', key: 'registrationId' },
    { label: 'Registrant ID', key: 'registrantId' },
    { label: 'Registration Payment', key: 'registrationPayment' }
  );

  // Add custom fields to the header
  if (customFields) {
    customFields.forEach((customField) => {
      csvHeader.push({ label: customField.label, key: customField.name });
    });
  }

  const auditExportParticipants = async () => {
    const response = await exportParticipants(orgId, seasonId, { exportType: 'audit_only' });

    if (response?.status === 200) {
      return true;
    } else {
      return false;
    }
  };

  const buildMembersExportData = () => {
    const memberExportData = [];
    const customDataByParticipant = groupBy('memberId', membersCustomData);

    membersToExport.map((member) => {
      const memberAddress = member.addresses[0];
      const memberEmailAddress = member.emailAddresses[0];

      member.seasonParticipants?.map((seasonParticipant) => {
        const memberDataToExport = {
          orgMemberId: member.id,
          first: member.firstName,
          last: member.lastName,
          role: seasonParticipant.participationRoles?.map((participationRole) => participationRole.name).join(', '),
          address: `${memberAddress?.line1 ?? ''} ${memberAddress?.line2 ?? ''}`.trim(),
          city: memberAddress?.city,
          state: memberAddress?.state,
          zip: memberAddress?.postalCode,
          birthdate: member.birthdate,
          jerseyNumber: seasonParticipant.jerseyNumber,
          position: seasonParticipant.position,
          email: memberEmailAddress?.email,
          phoneNumber: member.phoneNumbers[0]?.number,
          gender: member.gender,
          seasonParticipantId: seasonParticipant.seasonParticipantId,
        };

        if (memberContactsData) {
          const contacts = memberContactsData.filter((contact) => +contact.memberId === +member.id);
          contacts.forEach((contact, index) => {
            const contactIndex = index + 1;
            memberDataToExport[`p${contactIndex}fn`] = contact.firstName;
            memberDataToExport[`p${contactIndex}ln`] = contact.lastName;
            memberDataToExport[`p${contactIndex}relation`] = contact.label;
            memberDataToExport[`p${contactIndex}email`] = contact.emailAddresses[0]?.email;
            memberDataToExport[`p${contactIndex}home`] = getPhoneNumberByLabel(contact.phoneNumbers, 'home')?.number;
            memberDataToExport[`p${contactIndex}cell`] = getPhoneNumberByLabel(contact.phoneNumbers, 'mobile')?.number;
            memberDataToExport[`p${contactIndex}work`] = getPhoneNumberByLabel(contact.phoneNumbers, 'work')?.number;
          });
        }

        memberDataToExport['teamName'] = seasonParticipant.team?.name;
        memberDataToExport['teamId'] = seasonParticipant.team?.id;
        memberDataToExport['division'] = seasonParticipant.division?.name;
        memberDataToExport['pendingDivisionName'] = seasonParticipant.pendingDivisionName;
        memberDataToExport['pendingTeamName'] = seasonParticipant.pendingTeamName;
        memberDataToExport['registrationForm'] = seasonParticipant.registration?.registrationFormName;
        memberDataToExport['registrationId'] = seasonParticipant.registration?.id;
        memberDataToExport['registrantId'] = seasonParticipant.registrantId;
        memberDataToExport['registrationPayment'] = seasonParticipant.registration?.registrationPaymentStatus;

        customFields.forEach((customField) => {
          const memberCustomData = customDataByParticipant[seasonParticipant.seasonParticipantId]?.find(
            (customData) => +(customData.customFieldId || customData.leagueCustomFieldId) === +customField.id
          );
          memberDataToExport[customField.name] = memberCustomData?.value;
        });

        memberExportData.push(memberDataToExport);
      });
    });

    return memberExportData;
  };

  const prefixCSVFilename = seasonData?.showSeason
    ? `${seasonData.showSeason.program?.name}-${seasonData?.showSeason.name}`
    : '';

  const exportEnabled =
    membersToExport.length && seasonCustomFieldsData && membersCustomData && !loadingMembersCustomData;

  const actions = React.useMemo(
    () => [
      {
        label: exportEnabled ? 'Export Members' : 'Loading Export',
        icon: 'download',
        onClick: () => exportButtonRef.current.click(),
      },
      {
        label: 'Export Little League Rosters',
        icon: 'download',
        onClick: () => {
          setIsExporting(true);
          exportParticipants(orgId, seasonId, {
            exportType: 'little_league_baseball',
            seasonParticipantIds: filteredParticipantIds,
          })
            .then(({ data }) => generateCSVFileDownload(data.data))
            .catch(() => {
              reactiveFeedbackMessage({
                message: 'Something went wrong while trying export the data.',
                isError: true,
                autoclose: true,
              });
            })
            .finally(() => setIsExporting(false));
        },
      },
    ],
    [exportEnabled, filteredParticipantIds, orgId, seasonId]
  );

  const updateFilteredIds = React.useCallback(
    (x: number[]) => {
      if (!isEqual(x, filteredParticipantIds)) {
        setFilteredParticipantIds(x);
      }
    },
    [filteredParticipantIds]
  );

  return (
    <div id="participants" className="container hub-full-width">
      {isExporting && (
        <div className="full-size-overlay">
          <Loader type="spin" size={{ width: 40, height: 40 }} message="Exporting..." />
        </div>
      )}

      <div className="page-content u-xs-padXl u-flexExpandSides">
        <div className="page-content-header Grid u-padBottomSm">
          <div className="Grid-cell u-xs-size6of24">
            <h1>Participants</h1>
          </div>
          <div className="Grid-cell u-xs-size18of24 u-textRight header-buttons">
            <a href={`${TEAMSNAP_URL}/${divisionParam}/league_roster/new`}>
              <Button color="primary" icon="plus">
                Add Participant
              </Button>
            </a>
            <a href={`${TEAMSNAP_URL}/${divisionParam}/league_roster/roster_imports`}>
              <Button color="primary" icon="plus">
                Import Participants
              </Button>
            </a>

            {showLlbExport && (
              <div className="sui-inline sui-relative">
                <Button icon="download" color="primary" onClick={() => setExportIsOpen(true)}>
                  Export
                </Button>
                <ActionMenu
                  style={{ top: '30px', width: '265px' }}
                  align="right"
                  isOpen={exportIsOpen}
                  onClose={() => setExportIsOpen(false)}
                  actions={actions}
                />
              </div>
            )}
            <CSVLink
              ref={exportButtonRef}
              headers={csvHeader}
              data={buildMembersExportData}
              filename={`${prefixCSVFilename}-Participants.csv`}
              isDisabled={!exportEnabled}
              auditFunction={auditExportParticipants}
              otherprops={{ color: 'primary', style: { display: showLlbExport ? 'none' : 'inline' } }}
            >
              {exportEnabled ? 'Export Participants' : 'Loading Export'}
            </CSVLink>
          </div>
        </div>
        <div className="page-content-body u-padBottomXxl">
          <ParticipantsList
            orgId={+orgId}
            seasonId={+seasonId}
            programId={+programId}
            seasonData={seasonData}
            allMembers={!isLoading ? allMembers : []}
            isLoading={isLoading}
            setMembersToExport={setMembersToExport}
            customFields={customFields}
            setFilteredParticipantIds={updateFilteredIds}
          />
        </div>
      </div>
    </div>
  );
};

export default ParticipantsPage;
